import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { platform, i18n, type Tenant, type LocaleMetaDto } from '../lib/api.js'
import { Card, Button, Badge, toast, Spinner, Empty } from '../components/ui/index.js'
import { I18nRunsPanel } from '../components/I18nRunsPanel.js'
import { useI18n } from '../lib/i18n.js'

/**
 * Plataforma → Traduções (i18n).
 *
 * - Botão da BASE: traduz o dicionário de textos de interface (ui_texts,
 *   camada da plataforma) para os idiomas do catálogo sem dicionário — é o
 *   texto de todos os cardápios (botões, carrinho, checkout...).
 * - Botão POR RESTAURANTE: re-traduz o cardápio (produtos, categorias,
 *   ingredientes, variações, extras e a descrição) daquele tenant.
 */
export function PlatformTranslations() {
  const { t } = useI18n()
  const [busyTenant, setBusyTenant] = useState<string | null>(null)
  const qc = useQueryClient()
  // painel de execuções reage aos cliques sem precisar de refresh; a cobertura
  // também — a corrente do dicionário vai completando os idiomas aos poucos
  const kickRuns = () => {
    void qc.invalidateQueries({ queryKey: ['platform-i18n-runs'] })
    void qc.invalidateQueries({ queryKey: ['i18n-ui-texts-coverage'] })
  }

  const { data: tenants = [], isLoading: loadingTenants } = useQuery({
    queryKey: ['platform-tenants'],
    queryFn: platform.listTenants,
  })

  const { data: locales = [] } = useQuery({
    queryKey: ['i18n-locales'],
    queryFn: i18n.locales,
    staleTime: 5 * 60_000,
  })

  const bases = [...new Set((locales as LocaleMetaDto[]).map((l) => l.base))]

  // cobertura do dicionário de interface por idioma (chaves traduzidas/base)
  const { data: coverage = [], isLoading: loadingCoverage } = useQuery({
    queryKey: ['i18n-ui-texts-coverage'],
    queryFn: platform.uiTextsCoverage,
    staleTime: 60_000,
  })

  // base: preencher dicionários faltantes / forçar re-tradução dos rascunhos
  const baseMut = useMutation({
    mutationFn: (force: boolean) => platform.refreshUiTexts(force),
    onSuccess: (data) => {
      void kickRuns()
      toast.success(
        t('admin.translations.base_started_toast', {
          count: String(data.bases.length),
          bases: data.bases.join(', '),
        }),
      )
    },
    onError: (e: Error) => toast.error(e.message),
  })

  // por restaurante: re-traduz o cardápio do tenant
  const tenantMut = useMutation({
    mutationFn: (tenantId: string) => platform.refreshTenantI18n(tenantId),
    onMutate: (tenantId) => setBusyTenant(tenantId),
    onSettled: () => setBusyTenant(null),
    onSuccess: (data, tenantId) => {
      void kickRuns()
      const name =
        tenants.find((x) => x.id === tenantId)?.name ?? t('admin.translations.tenant_fallback')
      toast.success(
        t('admin.translations.tenant_started_toast', {
          name,
          count: String(data.invalidated),
        }),
      )
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* ─── Cobertura do dicionário por idioma ─── */}
      <Card
        title={t('admin.translations.coverage_title')}
        description={t('admin.translations.coverage_desc')}
      >
        {loadingCoverage ? (
          <div className="flex justify-center py-6">
            <Spinner className="w-5 h-5 text-indigo-400" />
          </div>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
            {coverage.map((lang) => {
              const done = lang.translated >= lang.total && lang.total > 0
              return (
                <li key={lang.base} className="flex items-center gap-2 py-1.5 text-sm">
                  <span className="flex-1 min-w-0 truncate">
                    {lang.native} <span className="text-gray-400">({lang.base})</span>
                  </span>
                  {done ? (
                    <Badge color="green">✓ {t('admin.translations.coverage_complete')}</Badge>
                  ) : (
                    <Badge color="yellow">
                      {t('admin.translations.coverage_missing', {
                        count: String(lang.total - lang.translated),
                      })}
                    </Badge>
                  )}
                  <span className="text-xs text-gray-400 tabular-nums">
                    {lang.translated}/{lang.total}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </Card>

      {/* ─── Base: dicionário de interface da plataforma ─── */}
      <Card
        title={t('admin.translations.base_title')}
        description={t('admin.translations.base_desc', { count: String(bases.length) })}
      >
        <div className="flex flex-wrap gap-2">
          <Button
            loading={baseMut.isPending && baseMut.variables === false}
            onClick={() => baseMut.mutate(false)}
          >
            {t('admin.translations.base_refresh')}
          </Button>
          <Button
            variant="secondary"
            loading={baseMut.isPending && baseMut.variables === true}
            onClick={() => baseMut.mutate(true)}
          >
            {t('admin.translations.base_force')}
          </Button>
        </div>
        <p className="text-xs text-gray-500">{t('admin.translations.base_hint')}</p>
      </Card>

      {/* ─── Por restaurante: cardápio de cada tenant ─── */}
      <Card
        title={t('admin.translations.per_tenant_title')}
        description={t('admin.translations.per_tenant_desc')}
      >
        {loadingTenants ? (
          <div className="flex justify-center py-8">
            <Spinner className="w-5 h-5 text-indigo-400" />
          </div>
        ) : tenants.length === 0 ? (
          <Empty
            title={t('admin.translations.empty_title')}
            description={t('admin.translations.empty_hint')}
          />
        ) : (
          <ul className="divide-y divide-gray-100">
            {tenants.map((tenant: Tenant) => (
              <TenantFeatureRow
                key={tenant.id}
                tenant={tenant}
                busy={busyTenant === tenant.id}
                onRefresh={() => tenantMut.mutate(tenant.id)}
                refreshPending={tenantMut.isPending}
              />
            ))}
          </ul>
        )}
      </Card>

      {/* ─── Execuções (base + restaurantes) com status ao vivo e logs ─── */}
      <Card
        title={t('admin.translations.runs_title')}
        description={t('admin.translations.runs_desc')}
      >
        <I18nRunsPanel
          queryKey={['platform-i18n-runs']}
          fetchRuns={platform.i18nRuns}
          fetchLogs={platform.i18nRunLogs}
          showScope
        />
      </Card>
    </div>
  )
}

// ─── Toggles de features por tenant (migration 0039) ─────────────────────────

type FeatureFlagDto = {
  feature: 'ai_translation' | 'menu_import_ocr' | 'promotions'
  enabled: boolean
}

const FEATURE_KEYS: Record<FeatureFlagDto['feature'], string> = {
  ai_translation: 'admin.features.ai_translation',
  menu_import_ocr: 'admin.features.menu_import_ocr',
  promotions: 'admin.features.promotions',
}

function TenantFeatureRow({
  tenant,
  busy,
  refreshPending,
  onRefresh,
}: {
  tenant: Tenant
  busy: boolean
  refreshPending: boolean
  onRefresh: () => void
}) {
  const { t } = useI18n()
  const qc = useQueryClient()

  const { data: features = [] } = useQuery({
    queryKey: ['tenant-features', tenant.id],
    queryFn: () => platform.listTenantFeatures(tenant.id),
  })

  const featureMut = useMutation({
    mutationFn: ({ feature, enabled }: FeatureFlagDto) =>
      platform.setTenantFeature(tenant.id, feature, enabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant-features', tenant.id] })
      toast.success(t('admin.features.updated_toast'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <li className="flex flex-wrap items-center gap-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 truncate">{tenant.name}</p>
        <p className="text-xs text-gray-500">
          {tenant.slug} · {t('admin.translations.primary_locale', { locale: tenant.locale })}
        </p>
      </div>
      {tenant.status !== 'active' && <Badge color="gray">{tenant.status}</Badge>}

      {/* toggles: clique alterna; verde = ativa */}
      <div className="flex gap-1.5">
        {features.map((f) => (
          <button
            key={f.feature}
            type="button"
            disabled={featureMut.isPending}
            title={f.enabled ? t('admin.features.enabled_hint') : t('admin.features.disabled_hint')}
            onClick={() => featureMut.mutate({ feature: f.feature, enabled: !f.enabled })}
            className={
              f.enabled
                ? 'rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-100'
                : 'rounded-full border border-gray-300 bg-white px-3 py-1 text-xs text-gray-500 hover:bg-gray-50'
            }
          >
            {f.enabled ? '● ' : '○ '}
            {t(FEATURE_KEYS[f.feature])}
          </button>
        ))}
      </div>

      <Button
        size="sm"
        variant="secondary"
        disabled={refreshPending}
        loading={busy}
        onClick={onRefresh}
      >
        {t('admin.translations.update_button')}
      </Button>
    </li>
  )
}
